import React, { useEffect, useRef } from 'react';
import PageBreadcrumb from '@/components/common/PageBreadCrumb';
import PageMeta from '@/components/common/PageMeta';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { useRentings } from '@/features/rentings/hooks/useRentings';
import { Renting } from '@/features/rentings/api/rentingsApi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader } from '@/components/ui/loader/Loader';
import { useTranslation } from 'react-i18next';

const RentiingsCalendarPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dateParam = searchParams.get('date');
  const highlightId = searchParams.get('highlight');
  const { data: rentingsData, isLoading } = useRentings();
  const calendarRef = useRef<any>(null);

  // Получаем начальную дату из URL или используем текущую
  const initialDate = dateParam || new Date().toISOString().split('T')[0];

  const highlightEvent = () => {
    if (highlightId && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const highlightedEvent = calendarApi.getEventById(highlightId);

      if (highlightedEvent) {
        // Добавляем класс для подсветки
        const eventElements = document.querySelectorAll(`[data-event-id="${highlightId}"]`);
        eventElements.forEach(eventEl => {
          const eventBlock = eventEl.closest('.fc-event');
          if (eventBlock) {
            // Прокручиваем к событию
            eventEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            // Запускаем анимацию после прокрутки
            setTimeout(() => {
              eventBlock.classList.add('highlight-selected');

              // Удаляем класс после завершения анимации
              setTimeout(() => {
                eventBlock.classList.remove('highlight-selected');
              }, 1000); // Время должно совпадать с длительностью анимации
            }, 500); // Даем время на прокрутку
          }
        });
      }
    }
  };

  const handleDatesSet = (dateInfo: any) => {
    const date = dateInfo.view.currentStart;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const newDate = `${year}-${month}-${day}`;

    navigate(`/rent-calendar?date=${newDate}`, { replace: true });

    // Запускаем подсветку после небольшой задержки, чтобы DOM точно обновился
    setTimeout(() => {
      highlightEvent();
    }, 100);
  };

  useEffect(() => {
    if (dateParam && highlightId && calendarRef.current) {
      // Только переключаем на нужный месяц
      const targetDate = new Date(dateParam);
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(targetDate);
      // Подсветка будет выполнена в handleDatesSet
    }
  }, [dateParam, highlightId, rentingsData]);

  function getEventColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return '#22c55e'; // green-500
      case 'draft':
        return '#4b5563'; // gray-600
      default:
        return '#4b5563'; // gray-600 по умолчанию
    }
  }

  const mapRentingToEvent = (renting: Renting) => {
    const color = getEventColor(renting.status);
    return {
      id: renting.id,
      title: renting.car.model.brand_name + ' ' + renting.car.model.model_name + ' ' + renting.car.registration_number || 'Unknown Car',
      start: `${renting.start_date}T${renting.start_time}`,
      end: `${renting.end_date}T${renting.end_time}`,
      backgroundColor: color,
      borderColor: color,
      textColor: '#fff',
      extendedProps: {
        status: renting.status,
        client: renting.client.first_name + ' ' + renting.client.last_name,
        address_to_deliver: renting.address_to_deliver,
        address_to_pickup: renting.address_to_pickup,
        car: renting.car,
        car_readable: renting.car.model.brand_name + ' ' + renting.car.model.model_name + ' ' + renting.car.registration_number
      }
    };
  };

  const events = React.useMemo(() => {
    if (!rentingsData?.data) return [];
    // Фильтруем, исключая отмененные аренды
    return rentingsData.data
      .filter(renting => renting.status.toLowerCase() !== 'cancelled')
      .map(mapRentingToEvent);
  }, [rentingsData]);

  const handleEventClick = (clickInfo: any) => {
    const rentingId = clickInfo.event.id;
    navigate(`/rentings/${rentingId}`);
  };

  function getDayEvents(date: Date, allEvents: any[]) {
    const dateStr = date.toISOString().split('T')[0];
    return allEvents.filter(event => {
      const isStart = event.start?.toISOString().split('T')[0] === dateStr;
      const isEnd = event.end?.toISOString().split('T')[0] === dateStr;
      return isStart || isEnd;
    });
  }

  function formatTime(timeStr: string): string {
    const [hours, minutes] = timeStr.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }

  const handleEventMouseEnter = (eventInfo: any) => {
    const eventId = eventInfo.event.id;
    const allEventElements = document.querySelectorAll(`[data-event-id="${eventId}"]`);

    allEventElements.forEach(element => {
      const eventBlock = element.closest('.fc-event') as HTMLElement;
      if (eventBlock) {
        eventBlock.style.opacity = '0.7';
        eventBlock.style.zIndex = '5';
      }
    });
  };

  const handleEventMouseLeave = (eventInfo: any) => {
    const eventId = eventInfo.event.id;
    const allEventElements = document.querySelectorAll(`[data-event-id="${eventId}"]`);

    allEventElements.forEach(element => {
      const eventBlock = element.closest('.fc-event') as HTMLElement;
      if (eventBlock) {
        eventBlock.style.opacity = '';
        eventBlock.style.zIndex = '';
      }
    });
  };

  if (isLoading) {
    return <Loader className='h-full w-full mt-60' />;
  }

  return (
    <>
      <PageMeta
        title={t('rentings.calendar.title')}
        description={t('rentings.calendar.description')}
      />
      <PageBreadcrumb
        items={[
          { label: t('rentings.calendar.title') }
        ]}
      />

      <div className="rounded-2xl border border-border-primary bg-bg-card px-5 py-6 shadow-default">
        <style>
          {`
            .fc-event {
              cursor: pointer;
              transition: opacity 0.2s;
            }
            .fc-event.highlight {
              opacity: 0.7;
            }
            .fc-dayGridMonth-view.fc-view {
              border: 1px solid #e5e7eb;
              border-top: none;
            }
            .fc-daygrid-day-frame {
              position: relative;
              min-height: 100px !important;
              display: flex;
              flex-direction: column;
            }
            .fc .fc-daygrid-day {
              min-height: 100px !important;
            }
            .fc .fc-daygrid-day-frame {
              height: 100% !important;
            }
            .fc .fc-scrollgrid-sync-table {
              min-height: 100%;
            }
            .fc .fc-daygrid-day-events {
              margin-bottom: 0 !important;
              padding-bottom: 40px !important;
              flex: 1;
            }
            .time-events-container {
              display: flex !important;
              flex-direction: row !important;
              flex-wrap: nowrap !important;
              gap: 8px !important;
              position: absolute;
              bottom: 4px;
              left: 8px;
              right: 8px;
              background: var(--bg-card);
              z-index: 1;
              padding: 4px;
              overflow-x: auto;
              white-space: nowrap;
            }
            .time-event {
              display: flex !important;
              align-items: center !important;
              font-size: 12px !important;
              cursor: pointer;
              padding: 2px 4px;
              border-radius: 4px;
              white-space: nowrap;
            }
            .time-event:hover {
              background-color: var(--bg-hover);
            }
            .fc-header-toolbar {
              padding-left: 0px !important;
              padding-right: 0px !important;
              padding-top: 0.5rem !important;
              align-items: start !important;
            }

            @media (max-width: 768px) {
              .fc-header-toolbar {
                margin-bottom: 0px !important;
              }
            }

            @keyframes double-flash {
              0%, 100% { filter: brightness(1); }
              25% { filter: brightness(1.3); }
              50% { filter: brightness(1); }
              75% { filter: brightness(1.3); }
            }

            .fc-event.highlight-selected {
              animation: double-flash 1s ease-in-out;
            }

            .fc-event {
              transition: all 0.2s ease-in-out;
            }
          `}
        </style>
        <div className="calendar-container">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={initialDate}
            firstDay={1}
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: ''
            }}
            editable={false}
            selectable={true}
            selectMirror={true}
            dayMaxEvents={true}
            eventDisplay="block"
            height="auto"
            events={events}
            eventContent={(eventInfo) => (
              <div
                className="p-1"
                data-event-id={eventInfo.event.id}
                title="Click to view rental details"
              >
                <div className="text-sm font-medium truncate">
                  {eventInfo.event.title}
                </div>
              </div>
            )}
            timeZone="UTC"
            dayCellDidMount={(args) => {
              const dayEvents = getDayEvents(args.date, args.view.calendar.getEvents());
              if (dayEvents.length > 0) {
                const timeContainer = document.createElement('div');
                timeContainer.className = 'time-events-container';

                dayEvents.forEach(event => {
                  const dateStr = args.date.toISOString().split('T')[0];
                  const eventStartStr = new Date(event.start).toISOString().split('T')[0];
                  const eventEndStr = new Date(event.end).toISOString().split('T')[0];

                  const isStart = eventStartStr === dateStr;
                  const isEnd = eventEndStr === dateStr;

                  if (isStart) {
                    const startDiv = document.createElement('div');
                    startDiv.className = 'text-text-secondary time-event';
                    startDiv.setAttribute('data-event-id', event.id);
                    startDiv.innerHTML = formatTime(event.startStr.split('T')[1]);

                    startDiv.addEventListener('mouseenter', () => {
                      const eventElements = document.querySelectorAll(`[data-event-id="${event.id}"]`);
                      eventElements.forEach(eventEl => {
                        const eventBlock = eventEl.closest('.fc-event');
                        eventBlock?.classList.add('highlight');
                      });
                    });

                    startDiv.addEventListener('mouseleave', () => {
                      const eventElements = document.querySelectorAll(`[data-event-id="${event.id}"]`);
                      eventElements.forEach(eventEl => {
                        const eventBlock = eventEl.closest('.fc-event');
                        eventBlock?.classList.remove('highlight');
                      });
                    });

                    timeContainer.appendChild(startDiv);
                  }

                  if (isEnd) {
                    const endDiv = document.createElement('div');
                    endDiv.className = 'text-text-secondary time-event';
                    endDiv.setAttribute('data-event-id', event.id);
                    endDiv.innerHTML = formatTime(event.endStr.split('T')[1]);

                    endDiv.addEventListener('mouseenter', () => {
                      const eventElements = document.querySelectorAll(`[data-event-id="${event.id}"]`);
                      eventElements.forEach(eventEl => {
                        const eventBlock = eventEl.closest('.fc-event');
                        eventBlock?.classList.add('highlight');
                      });
                    });

                    endDiv.addEventListener('mouseleave', () => {
                      const eventElements = document.querySelectorAll(`[data-event-id="${event.id}"]`);
                      eventElements.forEach(eventEl => {
                        const eventBlock = eventEl.closest('.fc-event');
                        eventBlock?.classList.remove('highlight');
                      });
                    });

                    timeContainer.appendChild(endDiv);
                  }
                });

                const frame = args.el.querySelector('.fc-daygrid-day-frame');
                if (frame) {
                  frame.appendChild(timeContainer);
                }
              }
            }}
            eventClick={handleEventClick}
            datesSet={handleDatesSet}
            eventMouseEnter={handleEventMouseEnter}
            eventMouseLeave={handleEventMouseLeave}
            locale={i18n.language === 'fr' ? frLocale : undefined}
          />
        </div>
      </div>
    </>
  );
};

export default RentiingsCalendarPage;
